import { WriterInfoServiceExtension } from '@blocksuite/affine/shared/services';
import { EXTERNAL_GUEST_PRESENCE_ID_PREFIX } from '@blocksuite/affine/shared/utils';
import type { DocImpl } from '@streamlain/workspace-editor/workspace/doc';
import { OnEvent, Service } from '@toeverything/infra';
import type { Awareness } from 'y-protocols/awareness.js';

import type { Workspace } from '../../workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import type { WorkspaceServerService } from './workspace-server';

export interface WriterWorkspace {
  readonly openOptions?: Pick<Workspace['openOptions'], 'externalDocAccess'>;
}

/** One writer identity binding for collection docs and local projections. */
@OnEvent(WorkspaceInitialized, i => i.onWorkspaceInitialized)
export class BlocksuiteWriterInfoService extends Service {
  private readonly configuredDocs = new WeakSet<DocImpl>();
  private readonly bindings = new Map<Awareness, WriterWorkspace>();
  private readonly unbinders = new WeakMap<Awareness, () => void>();
  private stopWatching?: () => void;

  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
    this.disposables.push(() => {
      for (const awareness of this.bindings.keys())
        this.unbinders.get(awareness)?.();
      this.stopWatching?.();
    });
  }

  private getWriterInfo(workspace: WriterWorkspace) {
    const external = workspace.openOptions?.externalDocAccess;
    if (external)
      return {
        id: `${EXTERNAL_GUEST_PRESENCE_ID_PREFIX}${external.identityId}`,
        name: external.displayName,
        avatar: undefined,
      };
    const account = this.workspaceServerService.server?.account$.value;
    return account
      ? {
          id: account.id,
          name: account.label?.trim() || account.email?.trim() || account.id,
          avatar: account.avatar,
        }
      : null;
  }

  bindAwareness(workspace: WriterWorkspace, awareness: Awareness): () => void {
    if (this.bindings.has(awareness))
      throw new Error('Awareness writer already bound');
    const update = (awareness: Awareness, workspace: WriterWorkspace) => {
      const writer = this.getWriterInfo(workspace);
      if (writer) awareness.setLocalStateField('user', writer);
    };
    this.bindings.set(awareness, workspace);
    update(awareness, workspace);
    if (!this.stopWatching) {
      const refresh = () =>
        this.bindings.forEach((workspace, awareness) =>
          update(awareness, workspace)
        );
      let accountSubscription: { unsubscribe(): void } | undefined;
      const bindAccount = () => {
        accountSubscription?.unsubscribe();
        accountSubscription =
          this.workspaceServerService.server?.account$.subscribe(refresh);
        refresh();
      };
      const serverSubscription =
        this.workspaceServerService.server$.subscribe(bindAccount);
      bindAccount();
      this.stopWatching = () => {
        serverSubscription.unsubscribe();
        accountSubscription?.unsubscribe();
        this.stopWatching = undefined;
      };
    }
    let disposed = false;
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      awareness.off('destroy', dispose);
      this.bindings.delete(awareness);
      this.unbinders.delete(awareness);
      if (!this.bindings.size) this.stopWatching?.();
    };
    this.unbinders.set(awareness, dispose);
    awareness.on('destroy', dispose);
    return dispose;
  }

  onWorkspaceInitialized(workspace: Workspace) {
    const setWriterInfo = (doc: DocImpl) => {
      if (this.configuredDocs.has(doc)) return;
      this.configuredDocs.add(doc);
      this.bindAwareness(workspace, doc.awarenessStore.awareness);
      doc.storeExtensions.push(
        WriterInfoServiceExtension({
          getWriterInfo: () => this.getWriterInfo(workspace),
        })
      );
    };
    workspace.docCollection.docs.forEach(doc => setWriterInfo(doc as DocImpl));
    const subscription = workspace.docCollection.slots.docAdded.subscribe(
      docId => {
        const doc = workspace.docCollection.docs.get(docId);
        if (doc) setWriterInfo(doc as DocImpl);
      }
    );
    const removed = workspace.docCollection.meta.docMetaRemoved.subscribe(
      docId => {
        for (const [awareness, owner] of this.bindings) {
          if (owner === workspace && awareness.doc.guid === docId)
            this.unbinders.get(awareness)?.();
        }
      }
    );
    this.disposables.push(() => removed.unsubscribe());
    this.disposables.push(() => subscription.unsubscribe());
  }
}
